import { Button, Stack, Typography } from "@mui/material"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import { Repeat } from "lib/@components/repeat"
import {
    AuthoringLegislationSelectorDialog,
    AuthoringLegislationSelectorSummary,
} from "authoring/routes/project/components/authoring-legislation-selector-dialog"
import { LEGISLATION_FOLDER } from "../legislation-library-helpers"
import { LegislationLibraryFolderPath } from "./legislation-library-folder-path"
import { LegislationLibraryManageLocationSummary } from "./legislation-library-manage-location-summary"

const LABEL_SX = { color: "text.secondary", fontWeight: 600, letterSpacing: 0.5 }

AuthoringLegislationSelectorSummary("folder").plug(<LegislationLibraryManageLocationSummary $lazy />)

export function LegislationLibraryFoldersField({ id }) {
    const { library } = useBoundContext()
    const { getGroupsForLegislation, getPath } = library
    const [folderIds, setFolderIds] = useBoundValue("folderIds", undefined)
    const currentFolderIds = folderIds ?? getGroupsForLegislation(id)
    const paths = currentFolderIds.map(getPath)

    const openManageFolders = useDialog(
        <AuthoringLegislationSelectorDialog
            initialSelection={currentFolderIds}
            tickableType={LEGISLATION_FOLDER}
            getSelectionKey={(node) => node.id}
            minSelected={1}
            minSelectedReason="Legislation must remain in at least one folder"
            title="Manage Location"
            rootLabel="Library Root"
            summaryVariant="folder"
            showFolderPath
            serializeSelection={(selected) => [...selected]}
        />,
        { maxWidth: "md", fullWidth: true }
    )

    return (
        <Stack spacing={1.5} data-cy="legislation-folders-field">
            <Button
                onClick={handleManage}
                data-cy="legislation-folders-manage"
                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
            >
                Manage Location…
            </Button>

            <Stack spacing={0.5}>
                <Typography variant="overline" sx={LABEL_SX}>
                    Appears in
                </Typography>
                <Stack spacing={0.5} data-cy="legislation-folders-list">
                    <Repeat list={paths} keyFn={pathKey} item={<LegislationLibraryFolderPath />} />
                </Stack>
            </Stack>
        </Stack>
    )

    async function handleManage() {
        const selected = await openManageFolders()
        if (selected !== undefined) setFolderIds(selected)
    }
}

function pathKey(path) {
    return path[path.length - 1]?.id
}
